import { useState, useEffect } from "react";
import { Menu, Settings, Bell, X } from "lucide-react";
import api from "@/api.js"; // adjust if your relative path differs

function Header({ pageTitle, onMenuClick, onSettingsClick }) {
  const [dateTime, setDateTime] = useState(new Date());

  // Notifications: pending SDA owners
  const [notifOpen, setNotifOpen] = useState(false);
  const [pendingOwners, setPendingOwners] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [pendingError, setPendingError] = useState("");

  // Review modal
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(false);

  // Decline reason
  const [declineReason, setDeclineReason] = useState("");

  useEffect(() => {
    const timer = setInterval(() => setDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Load pending SDA Owner registrations for the notification bell
  const fetchPendingOwners = async () => {
    try {
      setLoadingPending(true);
      setPendingError("");

      const res = await api.get("/api/admin/pending_owners");

      const data = res.data;
      const list = data?.pending_owners || data || [];
      setPendingOwners(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error loading pending SDA owners:", err);
      setPendingError("Failed to load pending SDA owners.");
    } finally {
      setLoadingPending(false);
    }
  };

  // Load once on mount
  useEffect(() => {
    fetchPendingOwners();
  }, []);

  // Open / Close review modal
  const openReviewModal = (owner) => {
    setSelectedOwner(owner);
    setDeclineReason("");
    setReviewOpen(true);
    setNotifOpen(false); // closes dropdown
  };

  const closeReviewModal = () => {
    setReviewOpen(false);
    setSelectedOwner(null);
    setDeclineReason("");
  };

  // Approve / Decline handler
  const handleOwnerDecision = async (userId, decision) => {
    setDecisionLoading(true);
    try {
      const endpoint =
        decision === "approve"
          ? `/api/admin/approve_owner/${userId}`
          : `/api/admin/decline_owner/${userId}`;

      if (decision === "decline") {
        const reason = (declineReason || "").trim();
        if (!reason) {
          alert("Please enter a reason for declining.");
          setDecisionLoading(false);
          return;
        }
        await api.post(endpoint, { reason });
      } else {
        await api.post(endpoint);
      }

      // Refresh list after approving/declining
      await fetchPendingOwners();
      closeReviewModal();
    } catch (err) {
      console.error(`Error trying to ${decision} owner:`, err);

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        "Request failed. Please check console.";
      alert(msg);
    } finally {
      setDecisionLoading(false);
    }
  };

  const formattedDate = dateTime.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const weekday = dateTime.toLocaleDateString("en-GB", { weekday: "long" });
  const formattedFullDate = `${formattedDate}, ${weekday}`;

  const formattedTime = dateTime.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <header className="h-16 bg-white border-b border-gray-200 px-6 flex items-center justify-between">
      {/* Left: title + mobile menu */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="md:hidden p-2 rounded-md hover:bg-gray-100"
          onClick={() => onMenuClick?.()}
          aria-label="Open sidebar">
          <Menu className="h-6 w-6 text-gray-700" />
        </button>
        <h1 className="text-gray-900 text-2xl font-semibold">{pageTitle}</h1>
      </div>

      {/* Right: date/time + icons */}
      <div className="flex items-center gap-3 text-gray-700">
        <div className="hidden sm:flex items-center gap-4 text-sm font-medium">
          <span>{formattedFullDate}</span>
          <span>{formattedTime}</span>
        </div>

        <div className="flex items-center gap-3">
          {/* Notifications bell */}
          <div className="relative">
            <button
              type="button"
              className="p-2 rounded-full bg-[#D2138C] hover:bg-[#b01173] transition cursor-pointer relative"
              onClick={() => setNotifOpen((v) => !v)}>
              <Bell className="h-5 w-5 text-white" />
              {pendingOwners.length > 0 && (
                <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-white text-[#D2138C] text-[10px] font-semibold flex items-center justify-center">
                  {pendingOwners.length}
                </span>
              )}
            </button>

            {/* Dropdown with pending SDA Owners */}
            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-800">
                    Pending SDA Owner registrations
                  </p>
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {loadingPending ? (
                    <p className="px-4 py-3 text-sm text-gray-500">Loading…</p>
                  ) : pendingError ? (
                    <p className="px-4 py-3 text-sm text-red-500">
                      {pendingError}
                    </p>
                  ) : pendingOwners.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-500">
                      No pending SDA Owners.
                    </p>
                  ) : (
                    pendingOwners.map((owner) => (
                      <div
                        key={owner.id}
                        className="px-4 py-3 border-b border-gray-100 last:border-b-0">
                        <p className="text-sm font-medium text-gray-900">
                          {owner.name || owner.full_name || owner.username}
                        </p>
                        <p className="text-xs text-gray-500">{owner.email}</p>

                        <div className="mt-2">
                          <button
                            type="button"
                            className="text-xs px-3 py-1 rounded-full bg-[#D2138C] text-white hover:bg-[#b01173]"
                            onClick={() => openReviewModal(owner)}>
                            View
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Settings button */}
          <button
            className="p-2 rounded-full bg-[#D2138C] hover:bg-[#b01173] transition cursor-pointer"
            onClick={onSettingsClick}>
            <Settings className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Review Modal */}
      {reviewOpen && selectedOwner && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/30"
            onClick={closeReviewModal}
          />

          {/* modal box */}
          <div className="relative w-full max-w-xl bg-white border border-gray-200 rounded-xl shadow-lg">
            <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">
                Review SDA Owner
              </p>

              <button
                type="button"
                className="p-2 rounded-full hover:bg-gray-100"
                onClick={closeReviewModal}>
                <X className="h-4 w-4 text-gray-700" />
              </button>
            </div>

            <div className="px-4 py-4">
              <div className="space-y-3 text-sm text-gray-800">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-medium text-gray-900">
                    {selectedOwner.name ||
                      selectedOwner.full_name ||
                      selectedOwner.username ||
                      "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Username</p>
                  <p className="font-medium text-gray-900">
                    {selectedOwner.username || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium text-gray-900 break-all">
                    {selectedOwner.email || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">
                    {selectedOwner.phone || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="font-medium text-gray-900">
                    {selectedOwner.status || "Pending"}
                  </p>
                </div>

                {/* Decline reason input */}
                <div>
                  <p className="text-xs text-gray-500">Decline reason</p>
                  <input
                    type="text"
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    placeholder="e.g. Incomplete documents"
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  disabled={decisionLoading}
                  className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={closeReviewModal}>
                  Close
                </button>

                <button
                  type="button"
                  disabled={decisionLoading}
                  className="text-xs px-3 py-1 rounded-full bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() =>
                    handleOwnerDecision(selectedOwner.id, "decline")
                  }>
                  {decisionLoading ? "Declining..." : "Decline"}
                </button>

                <button
                  type="button"
                  disabled={decisionLoading}
                  className="text-xs px-3 py-1 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() =>
                    handleOwnerDecision(selectedOwner.id, "approve")
                  }>
                  {decisionLoading ? "Approving..." : "Approve"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
